import React, { useContext, useEffect, useRef } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import { Ripple } from 'primereact/ripple';
import { classNames } from 'primereact/utils';
import { CSSTransition } from 'react-transition-group';
import { MenuContext } from './context/menucontext';
import { AppMenuItemProps } from '../types/types';

function usePrevious<T>(value: T): T | undefined {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}

const AppMenuItem: React.FC<AppMenuItemProps> = (props) => {
    const { activeMenu, setActiveMenu } = useContext(MenuContext);
    const location = useLocation();
    const item = props.item;
    const key = props.parentKey ? `${props.parentKey}-${props.index}` : String(props.index);
    const isActiveRoute = item!.to && location.pathname === item!.to;
    const active = activeMenu === key || activeMenu.startsWith(`${key}-`);
    const prevPath = usePrevious(location.pathname);

    useEffect(() => {

        if (item!.to && location.pathname === item!.to) {
            setActiveMenu(key);

            if (prevPath && prevPath !== location.pathname) {
                localStorage.removeItem(prevPath);
            }
        }
    }, [location.pathname]);

    const itemClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (item!.disabled) {
            event.preventDefault();
            return;
        }

        if (item!.command) {
            item!.command({ originalEvent: event, item });
        }

        if (item!.items) {
            setActiveMenu(active ? (props.parentKey as string) : key);
        } else {
            setActiveMenu(key);
        }
    };

    const subMenu = item!.items && item!.visible !== false && (
        <CSSTransition
            timeout={{ enter: 1000, exit: 450 }}
            classNames="layout-submenu"
            in={props.root ? true : active}
            key={item!.label}
        >
            <ul>
                {item!.items.map((child, i) => (
                    <AppMenuItem
                        item={child}
                        index={i}
                        className={child.badgeClass}
                        parentKey={key}
                        key={child.label}
                    />
                ))}
            </ul>
        </CSSTransition>
    );

    return (
        <li className={classNames({ 'layout-root-menuitem': props.root, 'active-menuitem': active })}>
            {props.root && item!.visible !== false && (
                <div className="layout-menuitem-root-text">{item!.label}</div>
            )}
            {(!item!.to || item!.items) && item!.visible !== false ? (
                <a
                    href={item!.url}
                    onClick={(e) => itemClick(e)}
                    className={classNames(item!.class, 'p-ripple')}
                    target={item!.target}
                    tabIndex={0}
                >
                    <i className={classNames('layout-menuitem-icon', item!.icon)}></i>
                    <b className="layout-menuitem-text">{item!.label}</b>
                    {item!.items && <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>}
                    <Ripple />
                </a>
            ) : null}

            {item!.to && !item!.items && item!.visible !== false ? (
                <NavLink
                    to={item!.to}
                    className={({ isActive }) =>
                        classNames(
                            item!.class,
                            'p-ripple',
                            { 'active-route': isActive }
                        )
                    }
                    onClick={(e) => itemClick(e)}
                    target={item!.target}
                    tabIndex={0}
                >
                    <i className={classNames('layout-menuitem-icon', item!.icon)}></i>
                    <b className="layout-menuitem-text">{item!.label}</b>
                    {item!.items && <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>}
                    <Ripple />
                </NavLink>
            ) : null}

            {subMenu}
        </li>
    );
};

export default AppMenuItem;
